'use strict';

/**
 * Merged-project tracker: Received → Publishing → Ready, with View details
 * opening the graph for MergeProjects → ProjectAvailable.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Collapse,
	Stepper,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Gray } from 'metabux/theme/colors';
import { MERGED_PROGRESS_COPY, PIPELINE_PROGRESS_UI_COPY } from './pipelineProgressCopy';
import { PipelineProgressStep } from './PipelineProgressStep';
import { PipelineStageDetails } from './PipelineStageDetails';

/** Shared reset for the app-wide MuiStepper theme override (see PipelineProgressSummary). */
const STEPPER_SX = {
	mb: 1,
	'& .MuiStepLabel-iconContainer svg': {
		width: 22,
		height: 22,
		border: 'none',
		borderRadius: 0,
		color: 'inherit',
	},
	'&.MuiStepper-horizontal .MuiStepConnector-root': {
		top: '11px',
		left: 'calc(-50% + 18px)',
		right: 'calc(50% + 18px)',
	},
	'&.MuiStepper-vertical .MuiStepConnector-root': { marginLeft: '11px' },
	'&.MuiStepper-vertical .MuiStepConnector-line': {
		border: 'none',
		borderLeft: '2px solid',
		borderColor: 'grey.300',
		minHeight: 16,
	},
	'& .MuiStepLabel-label, & .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
		color: 'text.primary',
		fontSize: '0.8125rem',
	},
};

/**
 * @param {{
 *   model: import('./pipelineProgressModel').PipelineProgressModel,
 *   stages?: import('./pipelineStatusTypes').PipelineStatusStage[],
 *   definition?: import('./pipelineStatusTypes').PipelineStatusGraphDefinition | null,
 * }} props
 */
export function MergedPipelineProgressSummary({ model, stages = [], definition = null }) {
	const theme = useTheme();
	const narrow = useMediaQuery(theme.breakpoints.down('md'));
	const failed = model.state === 'failed';
	const ready = model.state === 'ready';
	const unavailable = model.state === 'unavailable';
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedStepId, setSelectedStepId] = useState(/** @type {string | null} */ (null));

	useEffect(() => {
		setSelectedStepId(null);
	}, [model.currentStepId]);

	const effectiveStepId = selectedStepId ?? model.currentStepId ?? model.steps[0]?.id;
	const selectedStep = useMemo(
		() => model.steps.find((s) => s.id === effectiveStepId),
		[model.steps, effectiveStepId],
	);
	const activeStepIndex = Math.max(0, model.steps.findIndex((s) => s.id === model.currentStepId));

	const onSelectStep = (stepId) => {
		setSelectedStepId(stepId);
		setDetailsOpen(true);
	};

	return (
		<Box
			component="section"
			aria-label={MERGED_PROGRESS_COPY.panelTitle}
			data-testid="merged-progress-summary"
			sx={{ mt: 1.5, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
				<Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: Gray[600], flexGrow: 1, fontSize: '1rem' }}>
					{MERGED_PROGRESS_COPY.panelTitle}
				</Typography>
				{ready && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-testid="merged-progress-ready">
						<CheckCircleIcon color="success" fontSize="small" />
						<Typography variant="body2">{MERGED_PROGRESS_COPY.readyStepLabel}</Typography>
					</Box>
				)}
				{failed && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-testid="merged-progress-failed">
						<ErrorOutlineIcon color="error" fontSize="small" />
					</Box>
				)}
				{!unavailable && (
					<Button
						size="small"
						onClick={() => setDetailsOpen((v) => !v)}
						data-testid="merged-progress-details-toggle"
						aria-label={detailsOpen ? PIPELINE_PROGRESS_UI_COPY.hideDetails : PIPELINE_PROGRESS_UI_COPY.viewDetails}
					>
						{detailsOpen ? PIPELINE_PROGRESS_UI_COPY.hideDetails : PIPELINE_PROGRESS_UI_COPY.viewDetails}
					</Button>
				)}
			</Box>

			<Stepper
				nonLinear
				activeStep={unavailable ? -1 : activeStepIndex}
				orientation={narrow ? 'vertical' : 'horizontal'}
				alternativeLabel={!narrow}
				sx={STEPPER_SX}
			>
				{model.steps.map((step) => (
					<PipelineProgressStep
						key={step.id}
						step={step}
						isCurrent={!unavailable && step.id === model.currentStepId}
						onSelect={onSelectStep}
						inactive={unavailable}
					/>
				))}
			</Stepper>

			<Typography
				variant="body2"
				sx={{ color: failed ? 'error.main' : unavailable ? 'text.secondary' : Gray[800] }}
				aria-live="polite"
				role={failed ? 'alert' : undefined}
				data-testid="merged-progress-message"
			>
				{model.message}
			</Typography>

			{!unavailable && (
				<Collapse in={detailsOpen} unmountOnExit>
					{definition ? (
						<PipelineStageDetails step={selectedStep} stages={stages} />
					) : (
						<Alert
							severity="warning"
							sx={{ mt: 1.5 }}
							data-testid="merged-progress-details-missing-graph"
						>
							[pipelineStatus] Merged details need a PortalApi graph definition.
						</Alert>
					)}
				</Collapse>
			)}
		</Box>
	);
}
